import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Archive, Edit3, FileText, Link as LinkIcon, Plus,
  RefreshCw, Save, Settings, ShieldCheck, Upload, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import ModuleTitleRESOURCES from "@/components/resources/ModuleTitleRESOURCES";

const EMPTY_LINK = { title: "", description: "", category: "Helpful Links", external_url: "", visible_to_volunteers: false };
const EMPTY_FILE = { title: "", description: "", category: "Uncategorized", visible_to_volunteers: false, file: null };

export default function ResourcesAdmin() {
  const { backendUrl } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const [linkForm, setLinkForm] = useState(EMPTY_LINK);
  const [fileForm, setFileForm] = useState(EMPTY_FILE);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [editing, setEditing] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/resources", { params: { include_archived: showArchived } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const loadCats = async () => {
    try {
      const { data } = await api.get("/resources/categories");
      setCategories(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const createLink = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/resources/link", linkForm);
      toast.success("Link resource added.");
      setLinkForm(EMPTY_LINK);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setCreating(false); }
  };

  const uploadFile = async (e) => {
    e.preventDefault();
    if (!fileForm.file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", fileForm.title);
      fd.append("description", fileForm.description);
      fd.append("category", fileForm.category);
      fd.append("visible_to_volunteers", String(fileForm.visible_to_volunteers));
      fd.append("file", fileForm.file);
      const res = await fetch(`${backendUrl}/api/resources/file`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Upload failed");
      }
      toast.success("File resource uploaded.");
      setFileForm(EMPTY_FILE);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally { setUploading(false); }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive "${r.title}"? It stays in the audit trail and can be restored.`)) return;
    try {
      await api.post(`/resources/${r.id}/archive`);
      toast.success("Archived.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/resources/${r.id}/restore`);
      toast.success("Restored.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        title: editing.title,
        description: editing.description,
        category: editing.category,
        visible_to_volunteers: editing.visible_to_volunteers,
      };
      if (editing.type === "link") payload.external_url = editing.external_url;
      await api.put(`/resources/${editing.id}`, payload);
      toast.success("Resource updated.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const addCategory = async () => {
    const c = (categoryInput || "").trim();
    if (!c) return;
    const next = [...categories.filter((x) => x !== "Uncategorized"), c];
    try {
      const { data } = await api.put("/resources/categories", { categories: next });
      setCategories(data);
      setCategoryInput("");
      toast.success("Category added.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const removeCategory = async (c) => {
    if (!window.confirm(`Remove category "${c}"? Existing resources in that category will need to be recategorized.`)) return;
    const next = categories.filter((x) => x !== c && x !== "Uncategorized");
    try {
      const { data } = await api.put("/resources/categories", { categories: next });
      setCategories(data);
      toast.success("Category removed.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="resources-admin-page">
      <div>
        <Link to="/resources" className="text-sm text-forest-700 hover:text-forest-900 flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To Resources
        </Link>
        <ModuleTitleRESOURCES className="mt-2 patria-module-title text-lg sm:text-xl" />
        <h1 className="text-4xl font-serif text-forest-900 mt-2">Manage Resources</h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          Add playbooks, forms, guides, and helpful links for the team. Toggle "Visible to volunteers" for anything appropriate to share with volunteer teammates.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={uploadFile} className="patria-card p-6 space-y-4" data-testid="upload-file-form">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
              <Upload className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl text-forest-900">Upload A File</h2>
          </div>
          <Input label="Title *" value={fileForm.title} onChange={(v) => setFileForm({ ...fileForm, title: v })} testId="file-title" required />
          <Textarea label="Description" value={fileForm.description} onChange={(v) => setFileForm({ ...fileForm, description: v })} testId="file-description" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Category" value={fileForm.category} onChange={(v) => setFileForm({ ...fileForm, category: v })} options={categories} testId="file-category" />
            <div>
              <label className="patria-label">File *</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg"
                onChange={(e) => setFileForm({ ...fileForm, file: e.target.files?.[0] || null })}
                className="w-full text-sm"
                data-testid="file-input"
                required
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-charcoal-soft">
            <input
              type="checkbox"
              checked={fileForm.visible_to_volunteers}
              onChange={(e) => setFileForm({ ...fileForm, visible_to_volunteers: e.target.checked })}
              data-testid="file-visible-to-volunteers"
            />
            Visible to volunteers
          </label>
          <button type="submit" disabled={uploading} className="patria-btn-primary w-full" data-testid="btn-upload-file">
            {uploading ? "Uploading..." : "Upload File Resource"}
          </button>
        </form>

        <form onSubmit={createLink} className="patria-card p-6 space-y-4" data-testid="add-link-form">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center">
              <LinkIcon className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl text-forest-900">Add A Helpful Link</h2>
          </div>
          <Input label="Title *" value={linkForm.title} onChange={(v) => setLinkForm({ ...linkForm, title: v })} testId="link-title" required />
          <Textarea label="Description" value={linkForm.description} onChange={(v) => setLinkForm({ ...linkForm, description: v })} testId="link-description" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Category" value={linkForm.category} onChange={(v) => setLinkForm({ ...linkForm, category: v })} options={categories} testId="link-category" />
            <Input label="URL *" value={linkForm.external_url} onChange={(v) => setLinkForm({ ...linkForm, external_url: v })} testId="link-url" placeholder="https://" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-charcoal-soft">
            <input
              type="checkbox"
              checked={linkForm.visible_to_volunteers}
              onChange={(e) => setLinkForm({ ...linkForm, visible_to_volunteers: e.target.checked })}
              data-testid="link-visible-to-volunteers"
            />
            Visible to volunteers
          </label>
          <button type="submit" disabled={creating} className="patria-btn-primary w-full" data-testid="btn-add-link">
            {creating ? "Saving..." : "Add Link Resource"}
          </button>
        </form>
      </div>

      <div className="patria-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button
            className="text-sm text-forest-700 hover:text-forest-900 flex items-center gap-1"
            onClick={() => setShowCategories((v) => !v)}
            data-testid="btn-toggle-categories"
          >
            <Settings className="h-4 w-4" strokeWidth={1.5} /> {showCategories ? "Hide" : "Manage"} Categories
          </button>
          <label className="flex items-center gap-2 text-sm text-charcoal-soft">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived"
            />
            Show archived
          </label>
        </div>
        {showCategories && (
          <div className="mt-4 space-y-2" data-testid="categories-panel">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <span key={c} className="patria-chip-green inline-flex items-center gap-2">
                  {c}
                  {c !== "Uncategorized" && (
                    <button
                      onClick={() => removeCategory(c)}
                      className="text-terracotta-600 hover:text-terracotta-700"
                      data-testid={`remove-cat-${c}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input
                className="patria-input flex-1"
                placeholder="New category name..."
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                data-testid="new-category-input"
              />
              <button onClick={addCategory} className="patria-btn-secondary" data-testid="btn-add-category">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> Add
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="patria-card overflow-hidden">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Category</th>
              <th>Volunteers?</th>
              <th>Updated</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="italic text-charcoal-muted">Loading resources...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="italic text-charcoal-muted">Nothing here yet.</td></tr>
            )}
            {!loading && items.map((r) => (
              <tr key={r.id} className={r.archived ? "opacity-60" : ""} data-testid={`admin-row-${r.id}`}>
                <td>
                  <p className="font-medium text-forest-900">{r.title}</p>
                  <p className="text-xs text-charcoal-muted line-clamp-1">{r.description}</p>
                </td>
                <td>
                  <span className={r.type === "link" ? "patria-chip-terra" : "patria-chip-green"}>
                    {r.type === "link" ? <LinkIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                    {r.type}
                  </span>
                </td>
                <td className="text-sm">{r.category}</td>
                <td>
                  {r.visible_to_volunteers ? (
                    <span className="patria-chip-terra text-[10px]">
                      <ShieldCheck className="h-3 w-3" /> Yes
                    </span>
                  ) : <span className="text-xs text-charcoal-muted">No</span>}
                </td>
                <td className="text-xs text-charcoal-muted">{(r.updated_at || "").slice(0, 10)}</td>
                <td className="text-right space-x-2">
                  <button className="patria-btn-ghost" onClick={() => setEditing({ ...r })} data-testid={`btn-edit-${r.id}`}>
                    <Edit3 className="h-4 w-4" strokeWidth={1.5} /> Edit
                  </button>
                  {r.archived ? (
                    <button className="patria-btn-ghost text-forest-700" onClick={() => restore(r)} data-testid={`btn-restore-${r.id}`}>
                      <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> Restore
                    </button>
                  ) : (
                    <button className="patria-btn-ghost text-terracotta-600" onClick={() => archive(r)} data-testid={`btn-archive-${r.id}`}>
                      <Archive className="h-4 w-4" strokeWidth={1.5} /> Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="edit-drawer">
          <div className="absolute inset-0 bg-forest-900/40 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative ml-auto h-full w-full max-w-lg bg-cream-100 shadow-card overflow-y-auto p-6 animate-fade-up">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-forest-900">Edit Resource</h2>
              <button onClick={() => setEditing(null)} className="patria-btn-ghost">Cancel</button>
            </div>
            <div className="mt-6 space-y-4">
              <Input label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="edit-title" />
              <Textarea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="edit-description" />
              <Select label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={categories} testId="edit-category" />
              {editing.type === "link" && (
                <Input label="URL *" value={editing.external_url || ""} onChange={(v) => setEditing({ ...editing, external_url: v })} testId="edit-url" />
              )}
              <label className="flex items-center gap-2 text-sm text-charcoal-soft">
                <input
                  type="checkbox"
                  checked={!!editing.visible_to_volunteers}
                  onChange={(e) => setEditing({ ...editing, visible_to_volunteers: e.target.checked })}
                  data-testid="edit-visible-to-volunteers"
                />
                Visible to volunteers
              </label>
              <button onClick={saveEdit} className="patria-btn-primary w-full" data-testid="btn-save-edit">
                <Save className="h-4 w-4" strokeWidth={1.5} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testId, ...rest }) {
  return (
    <div>
      <label className="patria-label">{label}</label>
      <input
        className="patria-input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        {...rest}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="patria-label">{label}</label>
      <textarea
        className="patria-input w-full min-h-[80px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="patria-label">{label}</label>
      <select
        className="patria-input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
